import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { getDiagnosticsAnalyticsTimeSeries } from "../../../Api/BmsFaultsApi";
import ChartDrillDownModalV2 from "./ChartDrillDown/ChartDrillDownModalV2";
import { CHART_CONFIG, CHART_TYPES } from "./chartConfig";
import { calculateGranularity } from "../CommonFunctions";
import { formatDateLabel, formatDateForAPI, parseDateRangeFromLabel, filterTableColumnsConfigForSeverity } from "../../../Helper/Analytics/AnalyticsHelper";
import RenderChartLegend from "../../AdvancedAnalytics/ContinuousChart/RenderChartLegend";
import ScrollableChartWrapper from "./ScrollableColumnChart/ScrollableChartWrapper";
import { calculateNiceScale, generateYLabelsWithPositions } from "./ScrollableColumnChart/ChartScaleUtils";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import { useChartDrillDown } from "./hooks/useChartDrillDown";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import { useBmsMetaInfoFields, useBmsTableConfig } from "./useBmsTableConfig";


const SeverityOfFaultsChartView = ({ isSidebarExpanded, startDate, endDate, granularity, filters = [], hierarchicalLocation, fleetIds, onboardingLocations, allOnboardingLocations, alertCategories, operationalStatus, searchTerm, inDetailsView = false }) => {
	const { baseEntityType, baseEntityTypeIDLable, baseEntityTypeLable, baseEntityTypePluralLable } = useBaseEntityInfo();
	const [isResizing, setIsResizing] = useState(false);
	const [mounted, setMounted] = useState(false);
	const [loading, setLoading] = useState(true);
	const [chartData, setChartData] = useState({
		categories: [],
		warningData: [],
		highSeverityData: [],
		criticalData: [],
		vehiclesAffectedData: [],
		dateRanges: [] // Store original date ranges from API
	});

	const prevDepsRef = useRef('');

	const chartConfig = CHART_CONFIG[CHART_TYPES.SEVERITY]

	// Columns come from the bmsAlertV2 org meta when it configures them, and from
	// chartConfig otherwise, so an unconfigured org is unaffected.
	const resolveTable = useBmsTableConfig();
	const resolveMetaInfo = useBmsMetaInfoFields();

	// Configure drill-down hook with chart-specific overrides
	const drillDownConfig = useMemo(() => ({
		chartType: CHART_TYPES.SEVERITY,
		csvFileNamePrefix: "BmsFaults_FaultSeverity",

		// Level 1: Custom filters for severity
		getLevel1Filters: (dataPoint, baseFilters) => {
			return [...baseFilters, { name: "FaultSeverityName", values: [dataPoint.metricName] }];
		},
		getLevel1Select: () => ["GeneratedFaultsCount"],
		getLevel1MetaInfo: () => resolveMetaInfo(["vehicleCount"], "severitySummaryTable", chartConfig.popupConfig.summaryTable),
		getLevel1GroupBy: () => ["errorDescription", "FaultSeverityLevelID"],
		getTableConfig: (level, dataPoint) => {
			let table = chartConfig.popupConfig.summaryTable
			let tableKey = "severitySummaryTable"
			let emptyMessage = "No alerts found for the selected criteria"
			const severity = dataPoint?.metricName?.toLowerCase()

			if (level === 2) {
				table = chartConfig.popupConfig.vehiclesTable
				tableKey = "severityVehiclesTable"
				emptyMessage = `No ${baseEntityTypeLable} found for the selected criteria`
			}

			// Only the columns come from org meta; the title and empty message stay
			// with chartConfig so configuring columns cannot blank a heading.
			const resolved = resolveTable(tableKey, table);
			return {
				title: typeof table.title === 'function'
					? table.title(dataPoint)
					: table.title,
				tableColumnsConfig: filterTableColumnsConfigForSeverity(resolved.tableColumnsConfig, severity),
				tableColumnOrder: resolved.tableColumnOrder,
				emptyMessage: emptyMessage,
			};
		},
		getModalTitle: (dataPoint) => {
			const severityName = dataPoint?.metricName || "Faults";
			const category = formatDateRangeWithSmartYear(dataPoint?.startDate || startDate, dataPoint?.endDate || endDate);
			return (
				<Grid2 container gap={1} alignItems={"center"}>
					<Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
						Severity of Faults Triggered vs {baseEntityTypePluralLable} Impacted
						<Typography variant="fw500s16c000000" sx={{ lineHeight: "28px" }}>
							{' / '}{severityName}
						</Typography>
					</Typography>
					<Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>
						{'•'}
					</Typography>
					<Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>
						{category}
					</Typography>
				</Grid2>
			);
		},


		getCSVFileName: (dataPoint) => {
			const severityName = dataPoint?.metricName || "Faults";
			const formattedStartDate = new Date(dataPoint?.startDate || startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
			const formattedEndDate = new Date(dataPoint?.endDate || endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
			return `BmsFaults_FaultSeverity_${severityName}_${formattedStartDate}-${formattedEndDate}.csv`;
		},

		getLevel2Filters: (level2Record, dataPoint, baseFilters) => {
			const errorDescription = level2Record?.xLabel || level2Record?.faultName;
			return [
				...baseFilters,
				{ name: "FaultSeverityName", values: [dataPoint.metricName] },
				{ name: "errorDescription", values: [errorDescription] },
			];
		},
		getLevel2Select: () => ["GeneratedFaultsCount"],
		getLevel2MetaInfo: () => resolveMetaInfo(
			["licensePlateNumber", "originCountry", "originState", "originCity", "originDepo", "pingStatus"],
			"severityVehiclesTable",
			chartConfig.popupConfig.vehiclesTable,
		),
		getLevel2GroupBy: () => ["batteryID", "FaultSeverityLevelID"],
		level2GetCSVFileName: (level2Record, dataPoint) => {
			const faultName = level2Record?.xLabel || level2Record?.faultName || "Fault";
			const severityName = dataPoint?.metricName || "Faults";
			const formattedStartDate = new Date(dataPoint?.startDate || startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
			const formattedEndDate = new Date(dataPoint?.endDate || endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
			return `BmsFaults_${baseEntityTypePluralLable}_${faultName}_${severityName}_${formattedStartDate}-${formattedEndDate}.csv`;
		},
	}), [startDate, endDate, baseEntityTypePluralLable, resolveTable, resolveMetaInfo]);

	// Use the drill-down hook
	const drillDown = useChartDrillDown({
		startDate,
		endDate,
		hierarchicalLocation,
		fleetIds,
		alertCategories,
		operationalStatus,
		filters,
		config: drillDownConfig,
	});

	// Handle chart data point click - chart-specific logic
	const handleDataPointClick = (event, chartContext, config) => {
		const dataPointIndex = config.dataPointIndex;
		const seriesIndex = config.seriesIndex;

		const seriesNames = ["Warning", "High Severity", "Critical", `${baseEntityTypePluralLable} Affected`];
		const seriesName = seriesNames[seriesIndex];

		const category = chartData.categories[dataPointIndex];
		const originalDateRange = chartData.dateRanges[dataPointIndex];

		const { startDate: clickedStartDate, endDate: clickedEndDate } = parseDateRangeFromLabel(originalDateRange, startDate, endDate);

		// Get the value from raw data (chartData always contains raw values)
		let value = 0;
		switch (seriesIndex) {
			case 0:
				value = chartData.warningData[dataPointIndex] || 0;
				break;
			case 1:
				value = chartData.highSeverityData[dataPointIndex] || 0;
				break;
			case 2:
				value = chartData.criticalData[dataPointIndex] || 0;
				break;
			case 3:
				value = chartData.vehiclesAffectedData[dataPointIndex] || 0;
				break;
			default:
				value = 0;
		}

		const clickedData = {
			category: category,
			metricName: seriesName,
			value: value,
			dataPointIndex: dataPointIndex,
			seriesIndex: seriesIndex,
			startDate: clickedStartDate,
			endDate: clickedEndDate
		};

		// Use hook's handler
		drillDown.handleDataPointClick(clickedData);
	};

	const calculateDomains = () => {
		let faultValues = [];
		let vehiclesValues = [];

		if (chartData.categories.length > 0) {
			faultValues = [...chartData.warningData, ...chartData.highSeverityData, ...chartData.criticalData];
			vehiclesValues = chartData.vehiclesAffectedData;
		}

		const maxFault = Math.max(...faultValues, 0);
		const maxVehicles = Math.max(...vehiclesValues, 0);

		// Calculate nice scales for both axes
		const faultScale = calculateNiceScale(0, maxFault, 6); // Target 5 intervals (6 ticks)
		const vehiclesScale = calculateNiceScale(0, maxVehicles, 6);

		return {
			faultScale,
			vehiclesScale
		};
	};


	useEffect(() => {
		const fetchChartData = async () => {
			if (!startDate || !endDate) {
				setLoading(false);
				return;
			}

			// Create a string representation of all dependencies
			const currentDeps = JSON.stringify({
				startDate,
				endDate,
				granularity,
				filters,
				hierarchicalLocation,
				fleetIds,
				onboardingLocations,
				allOnboardingLocations,
				alertCategories,
				operationalStatus,
				searchDevices: searchTerm?.selectedDevices,
				searchColumn: searchTerm?.selectedSearchColumn
			});

			// Skip if dependencies haven't actually changed
			if (prevDepsRef.current === currentDeps) {
				return;
			}

			prevDepsRef.current = currentDeps;

			try {
				setLoading(true);

				// Calculate granularity based on date range if not provided
				let effectiveGranularity = granularity;
				if (!effectiveGranularity) {
					effectiveGranularity = calculateGranularity(startDate, endDate);
				}

				// Convert granularity to uppercase for API
				const apiGranularity = effectiveGranularity.toUpperCase();

				// Prepare global filters with hierarchical location, customer scope, alert
				// categories, and operational status. An absent fleetIds sends no fleetIDs
				// header, which is how this chart has always behaved.
				const globalFilters = {
					hierarchicalLocation: hierarchicalLocation,
					fleets: fleetIds,
					// The depot half of the same scope; the originLocations header is
					// derived from these two, so omitting them ignores a depot selection.
					onboardingLocations: onboardingLocations,
					allOnboardingLocations: allOnboardingLocations,
					alertCategories: alertCategories,
					operationalStatus: operationalStatus
				};

				// Prepare search filters (only if searchTerm is provided and has devices)
				const searchFilters = (searchTerm?.selectedDevices?.length > 0)
					? [{
						name: searchTerm.selectedSearchColumn || "searchID",
						values: searchTerm.selectedDevices
					}]
					: [];

				// Fetch severity data (Warning, High Severity, Critical)
				const severityFilters = {
					startDate: formatDateForAPI(startDate),
					endDate: formatDateForAPI(endDate),
					select: 'GeneratedFaultsCount',
					groupBy: 'FaultSeverityName',
					granularity: apiGranularity,
					filters: [
						...filters,
						...searchFilters
					],
				};

				// Fetch vehicles affected data
				const vehiclesFilters = {
					startDate: formatDateForAPI(startDate),
					endDate: formatDateForAPI(endDate),
					select: 'VehiclesAffectedCountForGeneratedFaults',
					granularity: apiGranularity,
					filters: [
						...filters,
						...searchFilters
					],
				};

				// Make both API calls in parallel
				const [severityResponse, vehiclesResponse] = await Promise.all([
					getDiagnosticsAnalyticsTimeSeries(globalFilters, severityFilters),
					getDiagnosticsAnalyticsTimeSeries(globalFilters, vehiclesFilters)
				]);

				const severityData = severityResponse?.response?.data || [];
				const vehiclesData = vehiclesResponse?.response?.data || [];

				// Transform API response to chart format
				const categories = [];
				const dateRanges = [];
				const warningData = [];
				const highSeverityData = [];
				const criticalData = [];
				const vehiclesAffectedData = [];

				// Process severity data
				severityData.forEach(item => {
					categories.push(formatDateLabel(item.xLabel));
					// Store original xLabel (date range) for later use
					dateRanges.push(item.xLabel);

					let warning = 0;
					let highSeverity = 0;
					let critical = 0;

					// Extract values from metricsArray based on severity type
					if (item.metricsArray && Array.isArray(item.metricsArray)) {
						item.metricsArray.forEach(metric => {
							const severityType = (metric.type || '').toLowerCase();

							if (severityType === 'warning' || severityType === 'warn') {
								warning = metric.value || 0;
							} else if (severityType === 'high severity' || severityType === 'high' || severityType === 'highseverity') {
								highSeverity = metric.value || 0;
							} else if (severityType === 'critical' || severityType === 'crit') {
								critical = metric.value || 0;
							}
						});
					}

					warningData.push(warning);
					highSeverityData.push(highSeverity);
					criticalData.push(critical);
				});

				// Process vehicles affected data and match with categories
				vehiclesData.forEach((item, index) => {
					let vehiclesAffected = 0;

					if (item.metricsArray && Array.isArray(item.metricsArray)) {
						item.metricsArray.forEach(metric => {
							const metricType = (metric.type || '').toLowerCase();
							if (metricType === 'vehicles') {
								vehiclesAffected = metric.value || 0;
							}
						});
					}

					vehiclesAffectedData.push(vehiclesAffected);
				});

				setChartData({
					categories,
					dateRanges,
					warningData,
					highSeverityData,
					criticalData,
					vehiclesAffectedData
				});
			} catch (error) {
				console.error("Error fetching severity chart data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchChartData();
	}, [startDate, endDate, granularity, filters, hierarchicalLocation, fleetIds, onboardingLocations, allOnboardingLocations, alertCategories, operationalStatus, searchTerm]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setMounted(true);
		}, 50);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (!mounted) return;
		setIsResizing(true);
		window.dispatchEvent(new Event('resize'));
		const timer = setTimeout(() => {
			window.dispatchEvent(new Event('resize'));
			setIsResizing(false);
		}, 350);

		return () => clearTimeout(timer);
	}, [isSidebarExpanded, mounted]);

	// Store raw data for labels and tooltips (only for bar series, not line)
	const rawWarningData = chartData.warningData;
	const rawHighSeverityData = chartData.highSeverityData;
	const rawCriticalData = chartData.criticalData;
	// Vehicles Affected is a line chart, keep as-is
	const vehiclesAffectedData = chartData.vehiclesAffectedData;

	// Calculate max value from all bar series data points
	const allBarValues = [...rawWarningData, ...rawHighSeverityData, ...rawCriticalData];
	const maxBarValue = Math.max(...allBarValues, 0);

	// Set minimum bar height for clickability (3% of max value)
	const NO_VALUE_MIN_HEIGHT = maxBarValue > 0 ? maxBarValue * 0.00 : 1;  // 0% for zero values
	const MIN_HEIGHT = maxBarValue > 0 ? maxBarValue * 0.08 : 1;          // ~8% of max as reasonable min height

	// Transform data: ensure visual bars are at least MIN_HEIGHT
	// For grouped bars, each series is independent
	const visualWarningData = rawWarningData.map((value) => {
		if (value === 0) return NO_VALUE_MIN_HEIGHT;
		return Math.max(value, MIN_HEIGHT);
	});

	const visualHighSeverityData = rawHighSeverityData.map((value) => {
		if (value === 0) return NO_VALUE_MIN_HEIGHT;
		return Math.max(value, MIN_HEIGHT);
	});

	const visualCriticalData = rawCriticalData.map((value) => {
		if (value === 0) return NO_VALUE_MIN_HEIGHT;
		return Math.max(value, MIN_HEIGHT);
	});

	// Calculate max value from visual bar data for yAxis
	const allVisualBarValues = [...visualWarningData, ...visualHighSeverityData, ...visualCriticalData];
	const visualMaxBarValue = Math.max(...allVisualBarValues, 0);

	// Recalculate fault scale with visual max value
	const visualFaultScale = calculateNiceScale(0, visualMaxBarValue, 6);

	// Vehicles scale remains unchanged (line chart doesn't need minimum height)
	const { vehiclesScale } = calculateDomains();

	// Chart dimensions for Y-axis label positioning
	const chartHeight = 315; // Height of the chart plot area
	const topOffset = 45; // Padding from top for the chart grid
	const bottomOffset = 37; // Padding from bottom for x-axis labels

	const faultLabels = generateYLabelsWithPositions(visualFaultScale, chartHeight, topOffset, bottomOffset);
	const vehiclesLabels = generateYLabelsWithPositions(vehiclesScale, chartHeight, topOffset, bottomOffset);

	// Calculate dynamic width based on number of data points
	const minWidth = chartData.categories.length * 80; // Minimum width per data point

	// Calculate how many categories to show at once
	const categoriesCount = chartData.categories.length;
	const maxVisibleCategories = Math.max(Math.floor(800 / 200), 3);

	const chartId = "severity-of-faults-chart";

	const chartOptions = {
		chart: {
			id: chartId,
			type: "bar",
			height: 315,
			toolbar: {
				show: false,
			},
			zoom: {
				enabled: categoriesCount > maxVisibleCategories,
				type: 'x',
				autoScaleYaxis: false,
			},
			animations: {
				enabled: true,
				dynamicAnimation: {
					enabled: true,
					speed: 300,
				},
				animateGradually: {
					enabled: true,
					delay: 0,
				},
				easing: 'easeinout',
			},
			events: {
				dataPointSelection: inDetailsView ? null : handleDataPointClick,
			},
		},
		stroke: {
			width: [0, 0, 0, 2], // [Warning, High Severity, Critical, Vehicles Affected line]
			curve: "straight",
		},
		markers: {
			size: [0, 0, 0, 3], // Show markers only for line chart (Vehicles Affected)
			strokeWidth: 0,
		},
		yaxis: [
			{
				seriesName: 'Warning',
				min: visualFaultScale.min,
				max: visualFaultScale.max,
				tickAmount: 5,
				forceNiceScale: false,
				labels: {
					formatter: (val) => val.toFixed(0)
				},
				show: false, // Hide ApexCharts Y-axis, use custom instead
			},
			{
				seriesName: 'Warning',
				min: visualFaultScale.min,
				max: visualFaultScale.max,
				tickAmount: 5,
				forceNiceScale: false,
				labels: {
					formatter: (val) => val.toFixed(0)
				},
				show: false,
			},
			{
				seriesName: 'Warning',
				min: visualFaultScale.min,
				max: visualFaultScale.max,
				tickAmount: 5,
				forceNiceScale: false,
				labels: {
					formatter: (val) => val.toFixed(0)
				},
				show: false,
			},
			{
				seriesName: `${baseEntityTypePluralLable} Affected`,
				min: vehiclesScale.min,
				max: vehiclesScale.max,
				tickAmount: 5,
				forceNiceScale: false,
				labels: {
					formatter: (val) => val.toFixed(0)
				},
				opposite: true,
				show: false, // Hide ApexCharts Y-axis, use custom instead
			},
		],
		plotOptions: {
			bar: {
				columnWidth: "24px",
				borderRadius: 2,
			},
		},
		states: {
			hover: {
				filter: {
					type: 'lighten',
					value: 0.1,
				}
			},
			active: {
				allowMultipleDataPointsSelection: false,
				filter: {
					type: 'none'
				}
			}
		},
		colors: ["#FFCA38", "#FF904B", "#B50500", "#4D44D8"],
		xaxis: {
			categories: chartData.categories,
			labels: {
				style: {
					colors: "#494949",
					fontSize: "12px",
				},
			},
			axisBorder: {
				color: "#E0E0E0",
			},
			axisTicks: {
				color: "#E0E0E0",
			},
			...(categoriesCount > maxVisibleCategories && {
				range: maxVisibleCategories,
			}),
		},
		legend: {
			show: false, // Hide default legend to use custom RenderChartLegend
		},
		grid: {
			show: true,
			borderColor: "#F0F0F0",
			strokeDashArray: 3,
			padding: {
				left: 0,
				right: 0,
				top: 15, // Explicit top padding to match axis offset
				bottom: 0, // Bottom padding for grid lines (not including x-axis)
			},
			xaxis: {
				lines: {
					show: false,
				}
			},
		},
		dataLabels: {
			enabled: false,
		},
		tooltip: {
			shared: true,
			intersect: false,
			followCursor: true,
			style: {
				fontSize: "12px",
			},
			y: {
				formatter: function (val, opts) {
					// Get actual values from raw data for tooltip
					const dataPointIndex = opts.dataPointIndex;
					const seriesIndex = opts.seriesIndex;

					let actualValue = val;
					// Series 0, 1, 2 are bars (Warning, High Severity, Critical)
					if (seriesIndex === 0) {
						actualValue = rawWarningData[dataPointIndex] || 0;
						return actualValue + " faults";
					} else if (seriesIndex === 1) {
						actualValue = rawHighSeverityData[dataPointIndex] || 0;
						return actualValue + " faults";
					} else if (seriesIndex === 2) {
						actualValue = rawCriticalData[dataPointIndex] || 0;
						return actualValue + " faults";
					} else if (seriesIndex === 3) {
						// Series 3 is line chart (Vehicles Affected) - use actual value
						actualValue = vehiclesAffectedData[dataPointIndex] || 0;
						return actualValue + ` ${baseEntityTypePluralLable.toLowerCase()}`;
					}

					return val;
				},
			},
		},
	};

	const legendSeries = [
		{ name: "Warning", color: "#FFCA38" },
		{ name: "High Severity", color: "#FF904B" },
		{ name: "Critical", color: "#B50500" },
		{ name: `${baseEntityTypePluralLable} Affected`, color: "#4D44D8" },
	];

	const series = [
		{
			name: "Warning",
			type: "column",
			data: visualWarningData,
		},
		{
			name: "High Severity",
			type: "column",
			data: visualHighSeverityData,
		},
		{
			name: "Critical",
			type: "column",
			data: visualCriticalData,
		},
		{
			name: `${baseEntityTypePluralLable} Affected`,
			type: "line",
			data: vehiclesAffectedData, // Line chart uses raw data (no minimum height needed)
		},
	];

	// Calculate if drill-down is enabled
	const drillDownEnabled = !inDetailsView && drillDown.getTableConfig(1);

	if (!mounted || isResizing || loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: chartHeight + 50,
				}}
			>
				<CartLoader />
			</Box>
		);
	}

	return (
		<Box sx={{ mt: 3, width: "100%", overflow: "hidden" }}>
			{/* Legend */}
			<Box sx={{ mb: 2 }}>
				<RenderChartLegend
					legendSeries={legendSeries}
					chartIDs={[chartId]}
					isLoading={loading}
					centered={true}
				/>
			</Box>

			{/* Chart Container - Scrollable with Fixed Y-Axes */}
			<ScrollableChartWrapper
				chartOptions={chartOptions}
				series={series}
				chartType="bar"
				chartHeight={chartHeight}
				leftAxis={{
					label: "Fault Count",
					labelData: faultLabels
				}}
				rightAxis={{
					label: `Number of ${baseEntityTypePluralLable} Affected`,
					labelData: vehiclesLabels
				}}
				isSidebarExpanded={isSidebarExpanded}
				minWidth={minWidth}
				categoriesCount={chartData.categories.length}
				drillDownEnabled={drillDownEnabled}
			/>
			{drillDown.getTableConfig(1) && (
				<ChartDrillDownModalV2
					open={drillDown.modalOpen}
					onClose={drillDown.handleModalClose}
					title={drillDown.getModalTitle}
					selectedDataPoint={drillDown.selectedDataPoint}
					tableConfigs={drillDown.getTableConfig(1)}
					getTableData={drillDown.getTableData}
					applyTableAction={drillDown.applyTableAction}
					getCSVFileName={drillDown.getCSVFileName}
					level2TableConfigs={drillDown.getTableConfig(2)}
					level2GetTableData={drillDown.level2GetTableData}
					level2ApplyTableAction={drillDown.level2ApplyTableAction}
					level2GetCSVFileName={drillDown.level2GetCSVFileName}
					level2Title={drillDown.level2Title}
				/>
			)}
		</Box>
	);
};

export default SeverityOfFaultsChartView;
